import { useEffect, useRef, useState } from 'react'
import { loadTurnstileSiteKey } from './turnstileConfig'

interface TurnstileRenderOptions {
  sitekey: string
  action: string
  theme: 'light'
  size: 'flexible'
  callback: (token: string) => void
  'expired-callback': () => void
  'error-callback': (code: string) => void
}

interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string
  remove(widgetId: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const scriptId = 'cloudflare-turnstile-script'
let scriptPromise: Promise<void> | null = null

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null
    const script = existing ?? document.createElement('script')

    const cleanup = () => {
      clearTimeout(timeout)
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
    }
    const handleLoad = () => {
      cleanup()
      if (window.turnstile) resolve()
      else { script.remove(); reject(new Error('TURNSTILE_SCRIPT_FAILED')) }
    }
    const handleError = () => { cleanup(); script.remove(); reject(new Error('TURNSTILE_SCRIPT_FAILED')) }
    const timeout = setTimeout(handleError, 15000)
    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })

    if (!existing) {
      script.id = scriptId
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      document.head.append(script)
    }
  }).catch((error) => {
    scriptPromise = null
    throw error
  })

  return scriptPromise
}

interface TurnstileWidgetProps {
  resetKey: number
  onVerify: (token: string) => void
  onExpire: () => void
  onError: (code?: string) => void
}

export function TurnstileWidget({
  resetKey,
  onVerify,
  onExpire,
  onError,
}: TurnstileWidgetProps) {
  const [retry, setRetry] = useState(0)
  const [failed, setFailed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const onVerifyRef = useRef(onVerify)
  const onExpireRef = useRef(onExpire)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onVerifyRef.current = onVerify
    onExpireRef.current = onExpire
    onErrorRef.current = onError
  }, [onError, onExpire, onVerify])

  useEffect(() => {
    let disposed = false
    let widgetId: string | undefined

    void Promise.all([loadTurnstileSiteKey(), loadTurnstileScript()])
      .then(([siteKey]) => {
        if (disposed || !containerRef.current || !window.turnstile) return
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'contact',
          theme: 'light',
          size: 'flexible',
          callback: (token) => { if (!disposed) { setFailed(false); onVerifyRef.current(token) } },
          'expired-callback': () => onExpireRef.current(),
          'error-callback': (code) => { if (!disposed) { setFailed(true); onErrorRef.current(code) } },
        })
      })
      .catch(() => {
        if (!disposed) { setFailed(true); onErrorRef.current() }
      })

    return () => {
      disposed = true
      if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId)
    }
  }, [resetKey, retry])

  return <><div className="turnstile-widget" ref={containerRef} />{failed && <button type="button" onClick={() => { onExpireRef.current(); setFailed(false); setRetry(value => value + 1) }}>Retry spam protection</button>}</>
}
